import React, {ChangeEvent, useEffect, useMemo, useState} from 'react';
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
    faBullhorn,
    faCalendarDays,
    faDove,
    faHouse,
    faLocationDot,
    faMapLocationDot,
    faSliders,
    faStar,
    faUpload
} from "@fortawesome/free-solid-svg-icons";
import {Link} from "react-router-dom";
import Button from "../../components/button";
import {BASE_MONTHS, SelectMonthOption} from "../../helpers/months";
import {getRandomDashboardHeroImage} from "../../helpers/dashboardHeroImages";
import type {DashboardRecentActivity} from "../../helpers/birdDataCache";
import AchievementMiniCard from "../discover/achievements/achievement-mini-card";
import {getAchievementSetDefinitionById} from "../discover/achievements/helpers";
import {DASHBOARD_WHATS_NEW_ITEMS} from "./constants";
import {formatDashboardYearOption, getDashboardIntroTier} from "./helpers";
import type {DashboardProps} from "./types";
import {PRE_EBIRD_LIST_BUILDING_YEAR} from "../../helpers/storedBirdData";
import {pluralize} from "../../helpers/helpers";
import CalculationLoading from "../../components/calculation-loading";
import './dashboard.scss';

export default function Dashboard({
    homeLocation,
    dashboardSummary,
    isDashboardSummaryLoading,
    storedDataRefreshedAt,
    storedCalibrationAppliedAt,
    years,
    months,
    onSetYear,
    onSetMonth,
    onSetTimePeriod,
    onResetStoredData,
    onShowHomeLocation,
    lifeListLength
}: DashboardProps) {
    const yearOptions = useMemo(() => {
        return (years as Array<number|string>)
            .map(year => Number(year))
            .filter((year): year is number => Number.isInteger(year) && year >= PRE_EBIRD_LIST_BUILDING_YEAR && year <= 2100)
            .sort((left, right) => right - left);
    }, [years]);
    const latestYear = useMemo(() => {
        const numericYears = yearOptions;
        return numericYears[0]?.toString() ?? '';
    }, [yearOptions]);
    const [year, setYear] = useState<string>('');
    const [month, setMonth] = useState<string>('null');
    const [filteredMonths, setFilteredMonths] = useState<SelectMonthOption[]>(BASE_MONTHS);
    const [isWhatsNewOpen, setIsWhatsNewOpen] = useState(false);
    const dashboardHeroImage = useMemo(() => {
        return getRandomDashboardHeroImage();
    }, []);

    useEffect(() => {
        if (!year && latestYear) {
            setYear(latestYear);
            onSetYear(latestYear);
        }
    }, [latestYear, onSetYear, year]);

    const dashboardYear = useMemo(() => {
        const parsedYear = parseInt(year);
        if (!Number.isNaN(parsedYear)) return parsedYear;

        const parsedLatestYear = parseInt(latestYear);
        if (!Number.isNaN(parsedLatestYear)) return parsedLatestYear;

        return Number.isNaN(parsedYear) ? undefined : parsedYear;
    }, [latestYear, year]);
    const dashboardYearLabel = useMemo(() => {
        if (!dashboardYear) return 'Your Year So Far';
        if (dashboardYear === PRE_EBIRD_LIST_BUILDING_YEAR) return formatDashboardYearOption(dashboardYear);

        const currentYear = new Date().getFullYear();
        return dashboardYear === currentYear
            ? `Your Year So Far (${dashboardYear})`
            : `Your Year Recap (${dashboardYear})`;
    }, [dashboardYear]);
    const dashboardYearDisplayLabel = dashboardYear ? formatDashboardYearOption(dashboardYear) : 'year';
    const yearStats = dashboardYear
        ? dashboardSummary?.yearStatsByYear[dashboardYear] ?? {species: 0, locations: 0, days: 0, lifers: 0}
        : {species: 0, locations: 0, days: 0, lifers: 0};
    const dashboardYearMapPath = dashboardYear
        ? `/map?year=${dashboardYear}&month=null`
        : '/map';
    const dashboardYearTourPath = dashboardYear
        ? `/map?year=${dashboardYear}&month=null&mode=year&tour=true`
        : '/map?mode=year&tour=true';
    const dashboardYearLifersMapPath = dashboardYear
        ? `/map?year=${dashboardYear}&month=null&mode=year&lifersOnly=true`
        : '/map?mode=year&lifersOnly=true';
    const dashboardHotspotExplorerPath = dashboardYear
        ? `/hotspot-explorer?year=${dashboardYear}&month=${month}`
        : '/hotspot-explorer';

    const recentActivity = useMemo((): DashboardRecentActivity[] => {
        return dashboardSummary?.recentActivity ?? [];
    }, [dashboardSummary]);
    const recentAchievements = useMemo(() => {
        return dashboardSummary?.recentAchievements ?? [];
    }, [dashboardSummary]);
    const inProgressAchievements = useMemo(() => {
        return dashboardSummary?.inProgressAchievements ?? [];
    }, [dashboardSummary]);
    const upcomingTargets = dashboardSummary?.upcomingTargets;
    const upcomingTargetItems = upcomingTargets ? [...upcomingTargets.dueNow, ...upcomingTargets.comingSoon] : [];

    const numYears = dashboardSummary?.numYears ?? 0;
    const dashboardIntro = useMemo(() => {
        const species = lifeListLength;
        const options = getDashboardIntroTier(species);
        const option = options[Math.floor(Math.random() * options.length)];

        return {
            heading: option.heading,
            description: option.description(species, numYears)
        };
    }, [lifeListLength, numYears]);

    const onMonthChange = (e: ChangeEvent) => {
        setMonth((e.target as HTMLInputElement).value);
    }

    const onYearChange = (e: ChangeEvent) => {
        const selectedYear = (e.target as HTMLInputElement).value;
        setYear(selectedYear);
        setMonth('null');
        onSetYear(selectedYear);
    }

    const handleSetTimePeriod = () => {
        if (!year) return;
        onSetYear(year);
        onSetMonth(month);
        onSetTimePeriod(year, month);
    }

    const handleResetStoredData = () => {
        onResetStoredData();
    }

    const handleShowHomeLocation = () => {
        onShowHomeLocation();
    }

    const toggleWhatsNew = () => {
        setIsWhatsNewOpen(isOpen => !isOpen);
    }

    useEffect(() => {
        if (!months?.length) return;

        const filteredMonths = BASE_MONTHS.filter((month: SelectMonthOption) => {
            const val: number = month.value;
            return (months as number[]).indexOf(val) > -1;
        });

        setFilteredMonths(filteredMonths);
    }, [months]);

    return (
        <section className="dashboard" aria-labelledby="dashboard-title">
            <div
                className="dashboard__hero"
                style={{'--dashboard-hero-image': `url("${dashboardHeroImage}")`} as React.CSSProperties}
            >
                <div className="dashboard__hero-content">
                    <p className="dashboard__eyebrow">Your birding data is ready to explore</p>
                    <h1 id="dashboard-title" className="dashboard__title">{dashboardIntro.heading}</h1>
                    <p className="dashboard__intro">{dashboardIntro.description}</p>

                    <div className="dashboard__filters" aria-label="Choose a time period">
                        <label className="dashboard__field">
                            <span>Show me birds from</span>
                            <select className="form-select" onChange={onYearChange} value={year}>
                                <option value="null">Select a Year</option>
                                <option value="life">Life List</option>
                                {yearOptions.map(year => (
                                    <option value={year} key={year}>{formatDashboardYearOption(year)}</option>
                                ))}
                            </select>
                        </label>

                        <label className="dashboard__field">
                            <span>During</span>
                            <select className="form-select" onChange={onMonthChange} value={month}>
                                <option value="null">All Months</option>
                                {filteredMonths.map(month => (
                                    <option value={month.value} key={month.label}>{month.label}</option>
                                ))}
                            </select>
                        </label>
                    </div>

                    <div className="dashboard__actions">
                        <Button btnClass="primary dashboard__path-btn" onClick={handleSetTimePeriod}>
                            <span className="dashboard__path-icon" aria-hidden="true">
                                <FontAwesomeIcon icon={faMapLocationDot} />
                            </span>
                            <span>
                                <strong>Explore as a Map</strong>
                                <span>See your sightings on the map</span>
                            </span>
                        </Button>
                        <Link className="btn btn-secondary dashboard__path-btn" to={dashboardYear ? `/calendar/${dashboardYear}${month !== 'null' ? `/${month.toString().padStart(2, '0')}` : ''}` : '/calendar'}>
                            <span className="dashboard__path-icon" aria-hidden="true">
                                <FontAwesomeIcon icon={faCalendarDays} />
                            </span>
                            <span>
                                <strong>Explore your Calendar</strong>
                                <span>See when and where you birded</span>
                            </span>
                        </Link>
                    </div>
                    <div className="dashboard__utility-row">
                        <div className="dashboard__utility-action">
                            <Button btnClass="link dashboard__reset-btn" onClick={handleResetStoredData}>
                                <FontAwesomeIcon icon={faUpload} aria-hidden="true" />
                                Upload a new data file
                            </Button>
                            {storedDataRefreshedAt ? (
                                <div className="dashboard__data-refresh">
                                    <span>eBird data refreshed</span>
                                    <time>{storedDataRefreshedAt}</time>
                                </div>
                            ) : null}
                        </div>
                        <Link className="dashboard__utility-link" to="/calibrate-data">
                            <FontAwesomeIcon icon={faSliders} aria-hidden="true" />
                            <span>
                                <strong>Calibrate your data</strong>
                                {storedCalibrationAppliedAt ? (
                                    <span className="dashboard__utility-meta">
                                        <span>Last calibrated</span>
                                        <time>{storedCalibrationAppliedAt}</time>
                                    </span>
                                ) : (
                                    <span>For more accuracy with exotic / naturalized species</span>
                                )}
                            </span>
                        </Link>
                        <div className="dashboard__home-location">
                            <FontAwesomeIcon icon={faHouse} aria-hidden="true" />
                            <span>
                                <strong>{homeLocation ? 'Home Location:' : 'No home location set'}</strong>
                                {homeLocation ?
                                    <span>
                                        {homeLocation.location},
                                        {' '}
                                        {[homeLocation.county, homeLocation.stateProvince, homeLocation.countryCode].filter(Boolean).join(', ')}
                                        {' '}
                                        (<button type="button" onClick={handleShowHomeLocation}>Change</button>)
                                    </span> :
                                    <span>
                                        Personalize your Life Journey
                                        {' '}
                                        (<button type="button" onClick={handleShowHomeLocation}>Set</button>)
                                    </span>
                                }
                            </span>
                        </div>
                    </div>
                </div>
                <div className="dashboard-whats-new-anchor">
                    <button
                        type="button"
                        className="dashboard-whats-new-toggle"
                        aria-expanded={isWhatsNewOpen}
                        aria-controls="dashboard-whats-new"
                        onClick={toggleWhatsNew}
                    >
                        <FontAwesomeIcon icon={faBullhorn} aria-hidden="true" />
                        <span>
                            <strong>See What&apos;s New</strong>
                            <span>in BirdMap Explorer</span>
                        </span>
                    </button>
                    <div
                        className={`dashboard-whats-new ${isWhatsNewOpen ? 'is-open' : ''}`}
                        id="dashboard-whats-new"
                        role="region"
                        aria-labelledby="dashboard-whats-new-title"
                        aria-hidden={!isWhatsNewOpen}
                    >
                        <div className="dashboard-whats-new__header">
                            <div>
                                <p>Latest updates</p>
                                <h2 id="dashboard-whats-new-title">What&apos;s new in BirdMap Explorer</h2>
                            </div>
                            <button type="button" onClick={toggleWhatsNew} tabIndex={isWhatsNewOpen ? 0 : -1}>Close</button>
                        </div>
                        <div className="dashboard-whats-new__body">
                            <ol>
                                {DASHBOARD_WHATS_NEW_ITEMS.map(item => (
                                    <li key={item.title}>
                                        <strong>{item.title}</strong>
                                        <span>{item.description}</span>
                                    </li>
                                ))}
                            </ol>
                        </div>
                    </div>
                </div>
            </div>

            {isDashboardSummaryLoading ? (
                <CalculationLoading
                    title="Preparing your dashboard"
                    message="Reviewing your sightings, recent activity, and achievement progress."
                />
            ) : (
            <div className="dashboard__panel-grid">
                <section className="dashboard-panel" aria-labelledby="year-stats-title">
                    <h2 id="year-stats-title">{dashboardYearLabel}</h2>
                    <div className="dashboard-stats">
                        <Link
                            className="dashboard-stat"
                            to={dashboardYearMapPath}
                            aria-label={`View ${dashboardYearDisplayLabel} species on the map`}
                        >
                            <span className="dashboard-stat__icon" aria-hidden="true">
                                <FontAwesomeIcon icon={faDove} />
                            </span>
                            <strong>{yearStats.species}</strong>
                            <span>Species Seen</span>
                        </Link>
                        <Link
                            className="dashboard-stat"
                            to={dashboardHotspotExplorerPath}
                            aria-label="View birded locations in Hotspot Explorer"
                        >
                            <span className="dashboard-stat__icon" aria-hidden="true">
                                <FontAwesomeIcon icon={faLocationDot} />
                            </span>
                            <strong>{yearStats.locations}</strong>
                            <span>{yearStats.locations === 1 ? 'Location' : 'Locations'} Birded</span>
                        </Link>
                        <Link
                            className="dashboard-stat"
                            to={dashboardYearTourPath}
                            aria-label={`View ${dashboardYearDisplayLabel} birding days in Tour Mode`}
                        >
                            <span className="dashboard-stat__icon" aria-hidden="true">
                                <FontAwesomeIcon icon={faCalendarDays} />
                            </span>
                            <strong>{yearStats.days}</strong>
                            <span>{yearStats.days === 1 ? 'Day' : 'Days'} Birded</span>
                        </Link>
                        <Link
                            className="dashboard-stat"
                            to={dashboardYearLifersMapPath}
                            aria-label={`View ${dashboardYearDisplayLabel} lifers on the map`}
                        >
                            <span className="dashboard-stat__icon" aria-hidden="true">
                                <FontAwesomeIcon icon={faStar} />
                            </span>
                            <strong>{yearStats.lifers}</strong>
                            <span>{yearStats.lifers === 1 ? 'Lifer' : 'Lifers'} Added</span>
                        </Link>
                    </div>
                    {upcomingTargets?.hasHomeLocation ? (
                        <div className="dashboard-upcoming-teaser">
                            <h3>Your Upcoming Targets</h3>
                            <Link className="dashboard-upcoming-teaser__body" to="/discover/upcoming-targets">
                                <p>{upcomingTargetItems.length ? <><strong>{upcomingTargetItems[0].commonName}</strong>{upcomingTargetItems.length > 1 ? ` and ${upcomingTargetItems.length - 1} other ${pluralize(upcomingTargetItems.length - 1, 'species')}` : ''} due now or upcoming soon.</> : 'No recurring nearby targets are due soon.'}</p>
                            </Link>
                            <Link className="dashboard-upcoming-teaser__cta" to="/discover/upcoming-targets">View Upcoming Targets</Link>
                        </div>
                    ) : null}
                </section>

                <section className="dashboard-panel" aria-labelledby="recent-activity-title">
                    <h2 id="recent-activity-title">Recent Activity</h2>
                    <div className="dashboard-activity">
                        {recentActivity.length ? recentActivity.map(activity => {
                            const linkedSpecies = activity.species.filter(species => Boolean(species.detailPath));

                            return (
                                <div className="dashboard-activity__item" key={activity.id}>
                                    <div className="dashboard-activity__header">
                                        <time>{activity.dateLabel}</time>
                                        {activity.locationDetailPath ? (
                                            <Link className="dashboard-activity__location-link" to={activity.locationDetailPath}>
                                                {activity.location}
                                            </Link>
                                        ) : (
                                            <strong>{activity.location}</strong>
                                        )}
                                        {activity.regionLabel ? <span>{activity.regionLabel}</span> : null}
                                    </div>
                                    {linkedSpecies.length ? (
                                        <div className="dashboard-activity__species-list" aria-label="Highlighted species">
                                            <span className="dashboard-activity__species-label">Species seen:</span>{' '}
                                            {linkedSpecies.map((species, index) => (
                                                <React.Fragment key={`${activity.id}-${species.label}`}>
                                                    {index > 0 ? ', ' : null}
                                                    <Link className="dashboard-activity__species-link" to={species.detailPath!}>
                                                        {species.label}
                                                    </Link>
                                                </React.Fragment>
                                            ))}
                                        </div>
                                    ) : null}
                                </div>
                            );
                        }) : (
                            <p className="dashboard-activity__empty">No recent checklists available yet.</p>
                        )}
                        <div className="dashboard-activity__footer">
                            <Link className="dashboard-achievements__cta" to="/hotspot-explorer">
                                View All Hotspots
                            </Link>
                        </div>
                    </div>
                </section>

                <section className="dashboard-panel dashboard-panel--placeholder" aria-labelledby="achievements-title">
                    <h2 id="achievements-title">Achievements</h2>
                    <div className="dashboard-achievements">
                        <div className="dashboard-achievements__section">
                            <h3>Recently Earned</h3>
                            {recentAchievements.length ? (
                                <div className="dashboard-achievements__recent-grid">
                                    {recentAchievements.map(achievement => (
                                        <div className="dashboard-achievement-mini-card" key={achievement.id}>
                                            <AchievementMiniCard
                                                title={achievement.title}
                                                dateLabel={achievement.earnedDateLabel}
                                                color={getAchievementSetDefinitionById(achievement.setId)?.color ?? achievement.color}
                                                icon={getAchievementSetDefinitionById(achievement.setId)?.icon ?? faStar}
                                            />
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="dashboard-achievements__empty">No achievements earned yet.</p>
                            )}
                        </div>

                        <div className="dashboard-achievements__section">
                            <h3>In Progress</h3>
                            {inProgressAchievements.length ? (
                                <div className="dashboard-achievements__list">
                                    {inProgressAchievements.map(achievement => (
                                        <div
                                            className="dashboard-achievement-item dashboard-achievement-item--progress"
                                            key={achievement.id}
                                            style={{'--achievement-color': getAchievementSetDefinitionById(achievement.setId)?.color ?? achievement.color} as React.CSSProperties}
                                        >
                                            <div className="dashboard-achievement-item__badge">
                                                <span
                                                    className="dashboard-achievement-item__hex"
                                                    aria-hidden="true"
                                                >
                                                    <FontAwesomeIcon icon={getAchievementSetDefinitionById(achievement.setId)?.icon ?? faStar} />
                                                </span>
                                            </div>
                                            <span className="dashboard-achievement-item__kicker">
                                                {getAchievementSetDefinitionById(achievement.setId)?.title ?? 'In Progress'}
                                            </span>
                                            <strong>{achievement.title}</strong>
                                            <div className="dashboard-achievement-item__progress">
                                                <span>{achievement.progressLabel}</span>
                                                <span>{achievement.percent}%</span>
                                            </div>
                                            <div
                                                className="dashboard-achievement-item__progress-bar achievement-progress"
                                                style={{'--achievement-progress': `${achievement.percent}%`, '--achievement-color': achievement.color} as React.CSSProperties}
                                            >
                                                <span />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="dashboard-achievements__empty">No in-progress achievements right now.</p>
                            )}
                        </div>
                        <div className="dashboard-achievements__footer">
                            <Link className="dashboard-achievements__cta" to="/discover/achievements">
                                View All Achievements
                            </Link>
                        </div>
                    </div>
                </section>
            </div>
            )}
        </section>
    )
}
